import React from 'react';
import { CircleCheckIcon, CircleSlashIcon } from 'lucide-react';
import moment from 'moment';
import { formatDateV2 } from 'utils/formatters/Date';
import Link from 'components/v2/Link.js';
import StatusBadge from './StatusBadge';
import { CustomerTypeBadge } from './CustomerTypeBadge';
import _ from 'lodash';
import { sluggify } from 'utils/localization/utils';
import { useTranslation } from 'react-i18next';
export const createCellFormatter = (formatter) => ({ column: { columnDef: { accessorKey }, }, row, }) => formatter(row.getValue(accessorKey));
export const formatLocationCell = (value) => (React.createElement("div", null, (value || '').replace(/,(?!\s)/g, ', ')));
export const formatStatusCell = ({ row }) => {
    const value = row.getValue('status');
    return React.createElement(StatusBadge, { status: value });
};
export const formatOriginCell = ({ row }) => {
    return React.createElement("div", null, row.getValue('origin').replace(/,(?!\s)/g, ', '));
};
export const formatDestinationCell = ({ row }) => {
    return React.createElement("div", null, row.getValue('destination').replace(/,(?!\s)/g, ', '));
};
export function formatBooleanValue(value) {
    if (value) {
        return (React.createElement("div", { className: "flex justify-end text-right" },
            React.createElement(CircleCheckIcon, { className: "w-4 h-4 text-green-500 text-right" })));
    }
    if (value === false) {
        return (React.createElement("div", { className: "flex justify-end text-right" },
            React.createElement(CircleSlashIcon, { className: "w-4 h-4 text-red-500 text-right" })));
    }
    return null;
}
export function formatTextValue(value) {
    return value || '-';
}
export function formatNumericValue(value) {
    return React.createElement("div", { className: "text-right" }, value || '-');
}
export function formatDollarValue(value) {
    if (!value)
        return formatNumericValue();
    const amount = parseFloat(value).toFixed(2);
    const formatted = new Intl.NumberFormat('en-us', {
        style: 'currency',
        currency: 'USD',
    }).format(amount);
    return formatNumericValue(formatted);
}
export const formatSequenceCell = ({ row }) => {
    const sequence = row.original.sequenceCount;
    const sequenceCount = row.original.totalCount;
    if (!sequence || !sequenceCount) {
        return '-';
    }
    return `${sequence} of ${sequenceCount} `;
};
export const formatCustomerTypeCell = ({ row }) => {
    const value = row.getValue('customerType');
    return React.createElement(CustomerTypeBadge, { customerType: value });
};
export function formatCustomerCell({ row }) {
    var _a;
    const customerId = (_a = row === null || row === void 0 ? void 0 : row.original) === null || _a === void 0 ? void 0 : _a.customerId;
    if (!customerId)
        return React.createElement("div", null, formatTextValue(null));
    return (React.createElement(Link, { url: `/users/${customerId}` }, formatTextValue(row.getValue('customerName'))));
}
export const formatRateCell = ({ row }) => {
    const rateValue = row.getValue('rate');
    if (!rateValue) {
        return formatNumericValue('-');
    }
    const amount = parseFloat(row.getValue('rate')).toFixed(2);
    const formatted = new Intl.NumberFormat('en-us', {
        style: 'currency',
        currency: 'USD',
    }).format(amount);
    return formatNumericValue(formatted);
};
export function formatDateCell(date) {
    const value = date ? moment.utc(date).format('MM/DD/YYYY') : '-';
    return value;
}
export const formatUpdatedAtCell = ({ row }) => {
    const rowValue = row.getValue('laneRatesUpdatedAt');
    return rowValue ? moment(rowValue).fromNow(true) : '-';
};
export const formatCreatedAtCell = ({ row }) => {
    const rowValue = row.getValue('laneRatesCreatedAt');
    return formatDateCell(rowValue);
};
export const formatValidityStart = ({ row }) => {
    const rowValue = row.getValue('effectiveStart');
    return formatDateCell(rowValue);
};
export const formatValidityEnd = ({ row }) => {
    const rowValue = row.getValue('effectiveEnd');
    return formatDateCell(rowValue);
};
export const formatReversibleCell = ({ row }) => {
    const reversible = row.getValue('reversible');
    return formatBooleanValue(reversible);
};
export const formatBillRoundtripCell = ({ row }) => {
    const rowValue = row.getValue('billRoundtrip');
    return formatBooleanValue(rowValue);
};
export const formatCommittmentCell = ({ row }) => {
    const value = row.getValue('commitment');
    return formatNumericValue(value);
};
export function getRateConditions(row) {
    var _a;
    return ((_a = row.original) === null || _a === void 0 ? void 0 : _a.rateConditions) || {};
}
export function formatHazardous(criteria) {
    return criteria;
}
export function getRateConditionCriteria(key, row) {
    var _a, _b;
    const { t } = useTranslation();
    const criteria = (_b = (_a = getRateConditions(row)) === null || _a === void 0 ? void 0 : _a[key]) === null || _b === void 0 ? void 0 : _b.criteria;
    if (['hazmat', 'hazardous'].includes(key))
        return formatHazardous(criteria);
    return t(sluggify(criteria));
}
export const formatCustomerRateConditionCell = ({ row }) => {
    var _a, _b;
    const customerCondition = getRateConditions(row).customer, customerId = customerCondition === null || customerCondition === void 0 ? void 0 : customerCondition.criteria, displayName = (_b = (_a = customerCondition === null || customerCondition === void 0 ? void 0 : customerCondition.meta) === null || _a === void 0 ? void 0 : _a.customer) === null || _b === void 0 ? void 0 : _b.name;
    if (!customerId)
        return React.createElement("div", null, formatTextValue(null));
    return (React.createElement(Link, { url: `/users/${customerId}` }, formatTextValue(displayName !== null && displayName !== void 0 ? displayName : customerId)));
};
export const formatEquipmenSizeRateConditionCell = ({ row }) => (React.createElement("div", { className: "text-right" }, formatTextValue(getRateConditionCriteria('equipmentSize', row))));
export const formatEquipmenTypeRateConditionCell = ({ row }) => formatTextValue(getRateConditionCriteria('equipmentType', row));
export const formatHazardousRateConditionCell = ({ row }) => formatTextValue(getRateConditionCriteria('hazardous', row));
export const formatMarketRateConditionCell = ({ row }) => formatTextValue(getRateConditionCriteria('market', row));
export const formatOrderTypeRateConditionCell = ({ row }) => formatTextValue(getRateConditionCriteria('orderType', row));
export const formatSteamshipLineRateConditionCell = ({ row }) => formatTextValue(getRateConditionCriteria('steamshipOrRailroad', row));
